import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, Share2, Check } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/lib/store";
import * as flow from "@/lib/flow";

export default function SupportSignalSheet({ result, onClose }) {
  const { content } = useApp();
  const [audience, setAudience] = useState(null);
  const [text, setText] = useState("");
  const [save, setSave] = useState(false);
  const [copied, setCopied] = useState(false);

  const pick = async (a) => {
    setAudience(a);
    try {
      const { text: t } = await flow.getSupportText(result, a.id);
      setText(t);
    } catch (e) { toast("Could not load message"); }
  };

  const persist = (channel) => {
    if (save && audience) {
      flow.getSupportText(result, audience.id, { save: true, savedText: text, channel }).catch(() => {});
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    persist("copy");
    toast("Copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const share = async () => {
    persist("share");
    if (navigator.share) {
      try { await navigator.share({ text }); } catch (e) { /* cancelled */ }
    } else {
      copy();
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-end justify-center"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      >
        <div className="absolute inset-0 bg-black/30" onClick={onClose} />
        <motion.div
          className="relative w-full max-w-md bg-[#FDFBF7] rounded-t-3xl p-6 pb-10 max-h-[88vh] overflow-y-auto"
          initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} transition={{ duration: 0.2 }}
          data-testid="support-signal-sheet"
        >
          <div className="flex items-start justify-between mb-4">
            <h2 className="font-serif-display text-3xl">Help me communicate what I need</h2>
            <button data-testid="support-close" onClick={onClose} className="ss-focus p-1" aria-label="Close support message">
              <X size={22} strokeWidth={1.5} />
            </button>
          </div>

          {!audience ? (
            <>
              <p className="text-sm text-[#474747] mb-5">Who is this for?</p>
              <div className="space-y-3">
                {content.support_audiences.map((a) => (
                  <button
                    key={a.id}
                    data-testid={`support-audience-${a.id}`}
                    onClick={() => pick(a)}
                    className="w-full text-left ss-card ss-tap ss-focus p-4 text-[15px] min-h-[52px]"
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <textarea
                data-testid="support-textarea"
                aria-label="Editable support message"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={6}
                className="w-full ss-card p-4 text-[15px] leading-relaxed resize-none ss-focus"
              />
              <label className="mt-3 flex items-center gap-2 text-[13px] text-[#474747]">
                <input
                  type="checkbox"
                  data-testid="support-save"
                  checked={save}
                  onChange={(e) => setSave(e.target.checked)}
                  className="accent-[#C9A227]"
                />
                Save this Support Signal to my history
              </label>
              <p className="mt-1 text-[12px] text-[#6B6B6B]">
                By default, edited text is not stored.
              </p>

              <div className="mt-5 flex gap-3">
                <button
                  data-testid="support-copy"
                  onClick={copy}
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-[#1A1A1A] rounded-full px-5 py-3 text-[15px] font-medium ss-tap ss-focus"
                >
                  {copied ? <Check size={17} /> : <Copy size={17} strokeWidth={1.5} />} Copy
                </button>
                <button
                  data-testid="support-share"
                  onClick={share}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-5 py-3 text-[15px] font-medium ss-tap ss-focus"
                >
                  <Share2 size={17} strokeWidth={1.5} /> Share
                </button>
              </div>
              <button
                data-testid="support-change-audience"
                onClick={() => { setAudience(null); setText(""); }}
                className="mt-4 text-[13px] text-[#6B6B6B] hover:text-[#1A1A1A] ss-focus transition-colors"
              >
                Choose a different audience
              </button>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
